import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import DownloadIcon from "@mui/icons-material/Download";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import MuiAlert from "@mui/material/Alert";

import axios from "axios";
import { useAuth } from "../../hooks/AuthProvider";
import { RCS_TEMPLATE_ANALYTICS_ENDPOINT } from "../../config/urls";
import RCSTemplatePreview from "../../common/RCSTemplatePreview";
import ButtonOne from "../../common/normal-button";
import AppDatePicker from "../../common/app-date-picker";
import AppChip from "../../common/app-chip";
import typography from "../../cssUtils/typography";
import ButtonTwo from "../../common/dashboard-button";
import { ColorsConstants } from "../../cssUtils/constants";

const RCSTemplateAnalyticsTab = ({ template, bot }) => {
  const [fromDate, setFromDate] = useState(dayjs().subtract(6, "day"));
  const [toDate, setToDate] = useState(dayjs());

  const { getValidToken } = useAuth();

  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);
  
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportMessageSeverity, setExportMessageSeverity] = useState("success");

  const [analyticsData, setAnalyticsData] = useState([]);
  const [dateRangeError, setDateRangeError] = useState(false);

  const [performanceMetrics, setPerformanceMetrics] = useState({
    messagesSent: { count: 0 },
    messagesDelivered: { count: 0 },
    messagesRead: { count: 0, readRate: 0 },
    messagesFailed: { count: 0 },
  });

  const [visibleSeries, setVisibleSeries] = useState({
    messagesSent: true,
    messagesDelivered: true,
    messagesRead: true,
    messagesFailed: true,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setChartError(null);
        setMetricsError(null);
        setChartLoading(true);
        setMetricsLoading(true);

        const formattedFromDate = fromDate.format("YYYY-MM-DD");
        const formattedToDate = toDate.format("YYYY-MM-DD");

        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");

        let queryParams = new URLSearchParams({
          from_date: formattedFromDate,
          to_date: formattedToDate,
        });

        const response = await axios.get(
          `${RCS_TEMPLATE_ANALYTICS_ENDPOINT}/${template.id}/chart/?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-Tenant-Id": tenant,
            },
          }
        );

        setAnalyticsData(response.data.analytics_data || []);
        
        if (response.data.performance_metrics) {
          setPerformanceMetrics(response.data.performance_metrics);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        const errMsg = error.response?.data?.error || "Failed to fetch analytics data";
        setChartError(errMsg);
        setMetricsError(errMsg);
        setAnalyticsData([]);
      } finally {
        setChartLoading(false);
        setMetricsLoading(false);
      }
    };

    if (template?.id && !dateRangeError) {
      fetchData();
    }
  }, [fromDate, toDate, template?.id, getValidToken]);

  const validateDateRange = (fromDate, toDate) => {
    return toDate.diff(fromDate, "day") <= 30;
  };

  const handleFromDateChange = (newDate) => {
    if (validateDateRange(newDate, toDate)) {
      setFromDate(newDate);
      setDateRangeError(false);
    } else {
      setFromDate(newDate);
      setDateRangeError(true);
      setChartError("Date range cannot be more than 30 days.");
      setMetricsError("Date range cannot be more than 30 days.");
    }
  };

  const handleToDateChange = (newDate) => {
    if (validateDateRange(fromDate, newDate)) {
      setToDate(newDate);
      setDateRangeError(false);
    } else {
      setToDate(newDate);
      setDateRangeError(true);
      setChartError("Date range cannot be more than 30 days.");
      setMetricsError("Date range cannot be more than 30 days.");
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    setExportMessage("");

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const formattedFromDate = fromDate.format("YYYY-MM-DD");
      const formattedToDate = toDate.format("YYYY-MM-DD");

      let queryParams = new URLSearchParams({
        from_date: formattedFromDate,
        to_date: formattedToDate,
      });

      const response = await fetch(
        `${RCS_TEMPLATE_ANALYTICS_ENDPOINT}/${template.id}/export/?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to export analytics data");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `rcs-analytics-${template.name}-${formattedFromDate}-to-${formattedToDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setExportMessage("Export completed successfully");
      setExportMessageSeverity("success");
    } catch (error) {
      console.error("Error exporting data:", error.message);
      setExportMessage("Failed to export analytics data. Please try again.");
      setExportMessageSeverity("error");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSeriesToggleClick = (event) => setAnchorEl(event.currentTarget);
  const handleSeriesToggleClose = () => setAnchorEl(null);
  
  const handleSeriesToggle = (series) => {
    setVisibleSeries((prev) => ({ ...prev, [series]: !prev[series] }));
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setExportMessage("");
  };

  const MetricDisplay = ({ label, count, readRate, colorClass }) => (
    <Box sx={{ textAlign: "center", p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
        <Typography variant="subtitle1" color="text.secondary">{label}</Typography>
        <Tooltip title={`Total ${label.toLowerCase()}`}>
          <InfoIcon fontSize="small" sx={{ ml: 1, color: "text.secondary", opacity: 0.7 }} />
        </Tooltip>
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center" }}>
        <Typography variant="h4" component="span" sx={{ mr: 1, fontWeight: 600, color: colorClass }}>
          {dateRangeError ? "NA" : count}
        </Typography>
        {readRate !== undefined && !dateRangeError && (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            ({readRate}%)
          </Typography>
        )}
      </Box>
    </Box>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, boxShadow: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{label}</Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" style={{ color: entry.color, fontWeight: 500 }}>
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, width: "100%", boxShadow: "none", bgcolor: "transparent" }}>
      
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ ...typography.h1 }}>
              {template?.name || "Template Name"}
            </Typography>
            <AppChip
              label={template?.status || "DRAFT"}
              color={template?.status === "APPROVED" ? "success" : "default"}
              sx={{ ml: 2 }}
            />
          </Box>
          
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {exportMessage && (
              <MuiAlert
                onClose={handleCloseSnackbar}
                severity={exportMessageSeverity}
                elevation={0}
                variant="filled"
                sx={{ height: "fit-content", py: 0.5, px: 1.5, fontSize: "0.85rem", borderRadius: 2 }}
              >
                {exportMessage}
              </MuiAlert>
            )}
            <ButtonOne
              variant="contained"
              startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleExportCSV}
              disabled={dateRangeError || isExporting}
            >
              Export CSV
            </ButtonOne>
          </Box>
        </Box>
        <Typography sx={{ ...typography.bodyLarge, color: "text.secondary", textTransform: "capitalize" }}>
          {template?.template_use_case?.replace(/_/g, " ") || "Promotional"} • {template?.template_type?.replace(/_/g, " ")}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3.5}>
          <Paper sx={{ p: 2, mb: 3, bgcolor: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "12px" }}>
            <Typography sx={{ ...typography.h3, mb: 2, color: ColorsConstants.primaryBlue100, textAlign: "center" }}>
              Live Preview
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", pb: 2 }}>
              <RCSTemplatePreview template={template} bot={bot} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8.5}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: "12px", border: "1px solid #E5E7EB", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
              <Typography sx={{ ...typography.h2 }}>Performance Summary</Typography>
              
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box>
                      <Typography variant="caption" sx={{ mb: 0.5, display: "block", color: "text.secondary" }}>From Date</Typography>
                      {/* 🟢 ADDED: format="DD/MM/YYYY" */}
                      <AppDatePicker format="DD/MM/YYYY" value={fromDate} onChange={handleFromDateChange} error={dateRangeError} slotProps={{ textField: { size: "small", sx: { width: 140 } } }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ mb: 0.5, display: "block", color: "text.secondary" }}>To Date</Typography>
                      {/* 🟢 ADDED: format="DD/MM/YYYY" */}
                      <AppDatePicker format="DD/MM/YYYY" value={toDate} onChange={handleToDateChange} error={dateRangeError} slotProps={{ textField: { size: "small", sx: { width: 140 } } }} />
                    </Box>
                  </Stack>
                </LocalizationProvider>
                {dateRangeError && (
                  <Typography color="error" variant="caption" sx={{ display: "block", mt: 1, textAlign: "right" }}>
                    Range cannot exceed 30 days
                  </Typography>
                )}
              </Box>
            </Box>

            {metricsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
            ) : metricsError ? (
              <Box sx={{ p: 3, textAlign: "center" }}><Typography color="error">{metricsError}</Typography></Box>
            ) : (
              <Grid container spacing={2} sx={{ borderTop: "1px solid #F3F4F6", pt: 2 }}>
                <Grid item xs={12} sm={3}>
                  <MetricDisplay label="Messages Sent" count={performanceMetrics.messagesSent.count} colorClass="#4B5563" />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MetricDisplay label="Delivered" count={performanceMetrics.messagesDelivered.count} colorClass="#651FFF" />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MetricDisplay label="Read" count={performanceMetrics.messagesRead.count} readRate={performanceMetrics.messagesRead.readRate} colorClass="#00BFA5" />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <MetricDisplay label="Failed" count={performanceMetrics.messagesFailed?.count || 0} colorClass="#FF5252" />
                </Grid>
              </Grid>
            )}
          </Paper>

          <Paper sx={{ p: 3, borderRadius: "12px", border: "1px solid #E5E7EB", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography sx={{ ...typography.h2 }}>Message Pipeline Over Time</Typography>

              <Box>
                <ButtonTwo variant="outlined" size="small" onClick={handleSeriesToggleClick} endIcon={<MoreVertIcon />}>
                  Toggle Metrics
                </ButtonTwo>
                {/* 🟢 FIXED: Removed FormControlLabel double-clicking bug */}
                <Menu anchorEl={anchorEl} open={open} onClose={handleSeriesToggleClose}>
                  <MenuItem onClick={() => handleSeriesToggle("messagesSent")}>
                    <Checkbox checked={visibleSeries.messagesSent} size="small" sx={{ py: 0, pl: 0, pr: 1 }} />
                    <Typography variant="body2">Sent</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleSeriesToggle("messagesDelivered")}>
                    <Checkbox checked={visibleSeries.messagesDelivered} size="small" sx={{ py: 0, pl: 0, pr: 1 }} />
                    <Typography variant="body2">Delivered</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleSeriesToggle("messagesRead")}>
                    <Checkbox checked={visibleSeries.messagesRead} size="small" sx={{ py: 0, pl: 0, pr: 1 }} />
                    <Typography variant="body2">Read</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleSeriesToggle("messagesFailed")}>
                    <Checkbox checked={visibleSeries.messagesFailed} size="small" sx={{ py: 0, pl: 0, pr: 1 }} />
                    <Typography variant="body2">Failed</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {chartLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
            ) : chartError ? (
              <Box sx={{ p: 3, textAlign: "center" }}><Typography color="error">{chartError}</Typography></Box>
            ) : (
              <Box sx={{ height: 320, width: "100%" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 12 }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                    <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} tickLine={false} axisLine={false} />
                    <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: "#F3F4F6" }} />
                    <Legend iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                    
                    {visibleSeries.messagesSent && (
                      <Line type="monotone" dataKey="messagesSent" stroke="#4B5563" strokeWidth={3} name="Sent" dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                    )}
                    {visibleSeries.messagesDelivered && (
                      <Line type="monotone" dataKey="messagesDelivered" stroke="#651FFF" strokeWidth={3} name="Delivered" dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                    )}
                    {visibleSeries.messagesRead && (
                      <Line type="monotone" dataKey="messagesRead" stroke="#00BFA5" strokeWidth={3} name="Read" dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                    )}
                    {visibleSeries.messagesFailed && (
                      <Line type="monotone" dataKey="messagesFailed" stroke="#FF5252" strokeWidth={3} name="Failed" dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

        </Grid>
      </Grid>
    </Paper>
  );
};

export default RCSTemplateAnalyticsTab;